import type { BookSourceLanguage, DictionarySourceProfile, InterfaceLocale } from "@readmf/contracts";
import { createContext, createElement, useContext, useMemo, type ReactNode } from "react";

type I18nValue = {
  locale: InterfaceLocale;
  copy: (english: string, russian: string) => string;
  path: (englishPath: string) => string;
  setLocale: (locale: InterfaceLocale) => void;
};

const I18nContext = createContext<I18nValue | null>(null);

export function localeFromPathname(pathname: string): InterfaceLocale {
  return pathname === "/ru" || pathname.startsWith("/ru/") ? "ru" : "en";
}

export function pathWithoutLocale(pathname: string): string {
  if (pathname === "/ru" || pathname === "/ru/") return "/";
  return pathname.startsWith("/ru/") ? pathname.slice(3) : pathname;
}

export function localizedPath(locale: InterfaceLocale, englishPath: string): string {
  if (locale === "en") return englishPath;
  return englishPath === "/" ? "/ru/" : `/ru${englishPath}`;
}

export function localizedLanguageName(language: BookSourceLanguage | InterfaceLocale, locale: InterfaceLocale): string {
  const names: Record<"en" | "es" | "it" | "ru", readonly [string, string]> = {
    en: ["English", "Английский"],
    es: ["Spanish", "Испанский"],
    it: ["Italian", "Итальянский"],
    ru: ["Russian", "Русский"],
  };
  return names[language][locale === "ru" ? 1 : 0];
}

export function dictionarySectionHeading(
  profile: DictionarySourceProfile,
  sourceLanguage: BookSourceLanguage,
  targetLanguage: InterfaceLocale,
  locale: InterfaceLocale,
): string {
  if (locale === "ru") {
    const sourcePrefixes: Record<BookSourceLanguage, string> = {
      en: "Англо",
      es: "Испано",
      it: "Итальяно",
    };
    const targetAdjectives: Record<BookSourceLanguage | InterfaceLocale, string> = {
      en: "английский",
      es: "испанский",
      it: "итальянский",
      ru: "русский",
    };
    const target = profile === "translation" ? targetLanguage : sourceLanguage;
    const heading = `${sourcePrefixes[sourceLanguage]}-${targetAdjectives[target]}`;
    return profile === "simplified" ? `${heading} (упрощённый)` : heading;
  }
  const source = localizedLanguageName(sourceLanguage, locale);
  const target = localizedLanguageName(profile === "translation" ? targetLanguage : sourceLanguage, locale);
  const heading = `${source}-to-${target}`;
  return profile === "simplified"
    ? `${heading} (simplified)`
    : heading;
}

export function I18nProvider({ locale, onLocaleChange, children }: {
  locale: InterfaceLocale;
  onLocaleChange: (locale: InterfaceLocale) => void;
  children: ReactNode;
}) {
  const value = useMemo<I18nValue>(() => ({
    locale,
    copy: (english, russian) => locale === "ru" ? russian : english,
    path: (englishPath) => localizedPath(locale, englishPath),
    setLocale: onLocaleChange,
  }), [locale, onLocaleChange]);
  return createElement(I18nContext.Provider, { value }, children);
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("I18nProvider is missing");
  return value;
}
